/* eslint-disable no-unused-vars */
import { createRef, useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import config from 'data/config.json';
import { useRegisterStore } from 'context/RegisterProvider/hooks';
import { getProvinces } from 'services/provinces';
import { createUser } from 'services/createUser';
import Notice from 'UI/atoms/Notice';
import CardContent from 'UI/atoms/CardContent';
import Form from 'UI/molecules/Form';
import { validateId } from 'services/validateId';
import { getCompanyDepartments } from 'services/getCompanyDepartments';
import { getWorkPositions } from 'services/getWorkPositions';
import Phone from 'UI/atoms/Phone';

const Step2Register = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { locale } = useParams();
  const zone = config.zones.find((zone) => zone.locale === i18n.language);

  const [registerState] = useRegisterStore();
  const [loading, setLoading] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [workPositions, setWorkPositions] = useState([]);
  const [validationSent, setValidationSent] = useState(false);

  const countryIdentifier = config.zones.find(
    (zone) => zone.id === registerState.zone
  )?.isoCode;

  const regex =
    /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ðºª ,.'-]*$/u;

  const validationSchema = Yup.object().shape(
    {
      name: Yup.string()
        .required(t('form.error.required'))
        .test('validation', t('form.error.invalid_name'), (val) => {
          return regex.test(val);
        }),
      surname1: Yup.string()
        .required(t('form.error.required'))
        .test('validation', t('form.error.invalid_name'), (val) => {
          return regex.test(val);
        }),
      surname2: Yup.string()
        .required(t('form.error.required'))
        .test('validation', t('form.error.invalid_name'), (val) => {
          return regex.test(val);
        }),
      legal_identifier: Yup.string()
        .required(t('form.error.required'))
        .test(
          'is-identifier-valid',
          t('form.step2.legal_identifier.invalid'),
          async (value) => {
            if (!value) return true;
            const result = await validateId({
              id: value,
              country_code: countryIdentifier,
            });
            const validTypes = [
              'NIF',
              'NIE',
              'Documento',
              'NIT',
              'DNI',
              'C.C./B.I.',
              'C.C.',
              'CIN',
              'ICE',
              'RUC',
            ];
            return result.valid && validTypes.includes(result.type);
          }
        ),
      company: Yup.string().required(t('form.error.required')),
      province: zone.provinceRequired
        ? Yup.string().required(t('form.error.required'))
        : Yup.string(),
      department: Yup.string()
        .test('len', t('form.error.required'), (val) => val.length > 0)
        .required(t('form.error.required')),
      workPosition: Yup.string()
        .test('len', t('form.error.required'), (val) => val.length > 0)
        .required(t('form.error.required')),
      otherWorkPosition: Yup.string()
        .ensure()
        .when('workPosition', {
          is: '-1',
          then: Yup.string()
            .test('len', t('form.error.required'), (val) => val.length > 0)
            .required(t('form.error.required')),
        }),
      phone: Yup.string()
        .test('len', t('form.error.required'), (val, context) => {
          const dialCode = context.parent?.countryPhone?.dialCode;
          if (!dialCode) return false;
          return val.slice(dialCode.length).length > 0;
        })
        .test('numbers', t('form.step2.phone.invalid'), (val, context) => {
          if (!val) return true;
          if (zone.validPhoneNumbers.length === 0) return true;
          const dialCode = context.parent?.countryPhone?.dialCode;
          const number = val.slice(dialCode.length);
          return zone.validPhoneNumbers.includes(parseInt(number[0], 10));
        }),
      password: Yup.string()
        .required()
        .test('len', t('form.error.invalid'), (val) => val.length > 7)
        .test('numbers', t('form.error.invalid'), (val) => {
          return /\d/.test(val);
        })
        .test('uppercase', t('form.error.invalid'), (val) => {
          return /[A-Z]/.test(val);
        }),
      confirmation_password: Yup.string()
        .required()
        .oneOf([Yup.ref('password'), null]),
      conditions: Yup.boolean().oneOf([true], t('form.error.required')),
      communication_acceptance: Yup.boolean().nullable().oneOf([true, false], t('form.error.required')),
    },
    ['workPosition', 'otherWorkPosition']
  );

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    setValue,
    watch,
    formState: { submitCount, errors },
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: registerState.registerData.email,
      country_identifier: countryIdentifier,
      zone_id: registerState.zone,
      password: '',
      confirmation_password: '',
      communication_acceptance: null,
    },
  });

  const activityId = registerState.activityId;
  const department = watch('department');
  const password = watch('password');
  const confirmationPassword = watch('confirmation_password');
  const workPosition = watch('workPosition');
  const phone = watch('phone');
  const countryPhone = watch('countryPhone');
  const workPositionInput = createRef();
  const baseUrl = `${window.location.protocol}//${
    window.location.host
  }/${locale}/validation${activityId ? `/${activityId}` : ''}`;

  const updatePhone = (value, country) => {
    setValue('phone', value);
    setValue('countryPhone', country);
  };

  const onSubmit = async (data) => {
    const userData = {
      name: data.name,
      surname1: data.surname1,
      surname2: data.surname2,
      email: data.email,
      password: data.password,
      zone_id: data.zone_id,
      communication_acceptance: data.communication_acceptance,
      legal_identifier: data.legal_identifier,
      country_identifier: data.country_identifier,
      company_name: data.company,
      province: data.province,
      phone: `+${data.phone}`,
      department: parseInt(data.department, 10),
      position: parseInt(data.workPosition, 10),
      position_custom: data.otherWorkPosition,
      locale,
      base_url: baseUrl,
    };
    setLoading(true);
    createUser(userData)
      .then(() => {
        setValidationSent(true);
      })
      .catch(() => {
        setError('apiError', { message: t('api.error') });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const filterByCallback = (option, search) =>
    option.name.toLowerCase().indexOf(search.text.toLowerCase()) === 0;

  useEffect(() => {
    getProvinces(registerState.zone).then((provinces) => {
      setProvinces(provinces);
    });
    getCompanyDepartments(countryIdentifier).then((departments) => {
      setDepartments(departments);
    });
  }, []);

  useEffect(() => {
    if (workPosition !== -1) {
      setValue('otherWorkPosition', '');
    }
  }, [workPosition]);

  useEffect(() => {
    if (validationSent) {
      navigate(`/${locale}/awaiting-validation`);
    }
  }, [validationSent]);

  useEffect(() => {
    workPositionInput.current.clear();

    setValue('workPosition', '');
    if (!department || department.length === 0) {
      return;
    }
    getWorkPositions(department, countryIdentifier).then((workPositions) => {
      workPositions.push({
        id: -1,
        name: t('form.step2.work_position.other'),
      });
      setWorkPositions(workPositions);
    });
  }, [department]);

  return (
    <CardContent sm={{ span: 10, offset: 1 }}>
      <div>
        <Notice color="blue" arrow>
          {t('form.step2.notice')}
        </Notice>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Input type="hidden" {...register('zone_id')} name="zone_id" />
          <Form.Input
            type="hidden"
            {...register('country_identifier')}
            name="country_identifier"
          />
          <Form.Field>
            <Form.Label htmlFor="form_name">
              {t('form.step2.name.label')}
            </Form.Label>
            <Form.Control>
              <Form.Input
                error={errors.name}
                disabled={loading}
                id="form_name"
                name="name"
                {...register('name')}
                placeholder={t('form.step2.name.placeholder')}
              />
              {errors.name && <Form.Error>{errors.name?.message}</Form.Error>}
            </Form.Control>
          </Form.Field>

          <Row>
            <Col xs={12} sm={6}>
              <Form.Field>
                <Form.Label htmlFor="form_surname1">
                  {t('form.step2.surname1.label')}
                </Form.Label>
                <Form.Control>
                  <Form.Input
                    error={errors.surname1}
                    disabled={loading}
                    id="form_surname1"
                    name="surname1"
                    {...register('surname1')}
                    placeholder={t('form.step2.surname1.placeholder')}
                  />
                  {errors.surname1 && (
                    <Form.Error>{errors.surname1?.message}</Form.Error>
                  )}
                </Form.Control>
              </Form.Field>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Field>
                <Form.Label htmlFor="form_surname2">
                  {t('form.step2.surname2.label')}
                </Form.Label>
                <Form.Control>
                  <Form.Input
                    error={errors.surname2}
                    disabled={loading}
                    id="form_surname2"
                    name="surname2"
                    {...register('surname2')}
                    placeholder={t('form.step2.surname2.placeholder')}
                  />
                  {errors.surname2 && (
                    <Form.Error>{errors.surname2?.message}</Form.Error>
                  )}
                </Form.Control>
              </Form.Field>
            </Col>
          </Row>

          <Form.Field>
            <Form.Label htmlFor="form_email">
              {t('form.step2.email.label')}
            </Form.Label>
            <Form.Control>
              <Form.Input
                disabled
                id="form_email"
                name="email"
                {...register('email')}
                placeholder={t('form.step2.email.placeholder')}
              />
            </Form.Control>
          </Form.Field>

          <Row>
            <Col xs={12} sm={6}>
              <Form.Field>
                <Form.Label htmlFor="form_legal_identifier">
                  {t('form.step2.legal_identifier.label')}
                </Form.Label>
                <Form.Control>
                  <Form.Input
                    error={errors.legal_identifier}
                    disabled={loading}
                    id="form_legal_identifier"
                    name="legal_identifier"
                    {...register('legal_identifier')}
                    placeholder={t('form.step2.legal_identifier.placeholder')}
                  />
                  {errors.legal_identifier && (
                    <Form.Error>{errors.legal_identifier?.message}</Form.Error>
                  )}
                </Form.Control>
              </Form.Field>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Field>
                <Form.Label htmlFor="form_company">
                  {t('form.step2.company.label')}
                </Form.Label>
                <Form.Control>
                  <Form.Input
                    error={errors.company}
                    disabled={loading}
                    id="form_company"
                    name="company"
                    {...register('company')}
                    placeholder={t('form.step2.company.placeholder')}
                  />
                  {errors.company && (
                    <Form.Error>{errors.company?.message}</Form.Error>
                  )}
                </Form.Control>
              </Form.Field>
            </Col>
          </Row>

          <Row>
            <Col xs={12} sm={6}>
              <Form.Field>
                <Form.Label htmlFor="form_department">
                  {t('form.step2.department.label')}
                </Form.Label>
                <Form.Control>
                  <Form.Input
                    type="hidden"
                    {...register('department')}
                    name="department"
                  />
                  <Form.Autocomplete
                    id="form_department"
                    disabled={loading}
                    error={errors.department}
                    labelKey={(option) => option.name}
                    filterBy={filterByCallback}
                    onChange={(selectedOptions) => {
                      if (selectedOptions.length === 0) {
                        setValue('department', '');
                        return;
                      }
                      setValue('department', selectedOptions[0].id);
                    }}
                    renderMenuItemChildren={(option) => option.name}
                    placeholder={t('form.step2.department.placeholder')}
                    options={departments}
                  />
                  {errors.department && (
                    <Form.Error>{errors.department?.message}</Form.Error>
                  )}
                </Form.Control>
              </Form.Field>
            </Col>

            <Col xs={12} sm={6}>
              <Form.Field>
                <Form.Label htmlFor="form_work_position">
                  {t('form.step2.work_position.label')}
                </Form.Label>
                <Form.Control>
                  <Form.Input
                    type="hidden"
                    {...register('workPosition')}
                    name="work_position"
                  />
                  <Form.Autocomplete
                    id="form_work_position"
                    ref={workPositionInput}
                    disabled={
                      loading || !department || workPositions.length === 0
                    }
                    error={errors.workPosition}
                    labelKey={(option) => option.name}
                    filterBy={filterByCallback}
                    onChange={(selectedOptions) => {
                      if (selectedOptions.length === 0) {
                        setValue('workPosition', '');
                        return;
                      }
                      setValue('workPosition', selectedOptions[0].id);
                    }}
                    renderMenuItemChildren={(option) => option.name}
                    placeholder={
                      loading || !department
                        ? t('form.step2.work_position.placeholder')
                        : ''
                    }
                    options={workPositions}
                  />
                  {errors.workPosition && (
                    <Form.Error>{errors.workPosition?.message}</Form.Error>
                  )}
                </Form.Control>
              </Form.Field>
            </Col>
          </Row>

          {workPosition === -1 && (
            <Row>
              <Col>
                <Form.Field>
                  <Form.Label htmlFor="form_other_work_position">
                    {t('form.step2.other_work_position.label')}
                  </Form.Label>
                  <Form.Control>
                    <Form.Input
                      error={errors.otherWorkPosition}
                      disabled={loading}
                      id="form_other_work_position"
                      name="other_work_position"
                      {...register('otherWorkPosition')}
                      placeholder={t(
                        'form.step2.other_work_position.placeholder'
                      )}
                    />
                    {errors.otherWorkPosition && (
                      <Form.Error>
                        {errors.otherWorkPosition?.message}
                      </Form.Error>
                    )}
                  </Form.Control>
                </Form.Field>
              </Col>
            </Row>
          )}

          <Row>
            {zone.provinceRequired && (
              <Col xs={12} sm={6}>
                <Form.Field>
                  <Form.Label htmlFor="form_province">
                    {t('form.step2.province.label')} *
                  </Form.Label>
                  <Form.Control>
                    <Form.Input
                      type="hidden"
                      {...register('province')}
                      name="province"
                    />
                    <Form.Autocomplete
                      id="form_province"
                      disabled={loading}
                      error={errors.province}
                      labelKey={(option) => option.name}
                      filterBy={filterByCallback}
                      onChange={(selectedOptions) => {
                        if (selectedOptions.length === 0) {
                          setValue('province', '');
                          return;
                        }
                        setValue('province', selectedOptions[0].name);
                      }}
                      renderMenuItemChildren={(option) => (
                        <div>{option.name}</div>
                      )}
                      placeholder={t('form.step2.province.placeholder')}
                      options={provinces}
                    />
                    {errors.province && (
                      <Form.Error>{errors.province?.message}</Form.Error>
                    )}
                  </Form.Control>
                </Form.Field>
              </Col>
            )}
            <Col xs={12} sm={zone.provinceRequired ? 6 : 12}>
              <Form.Field>
                <Form.Label htmlFor="form_phone">
                  {t('form.step2.phone.label')}
                </Form.Label>
                <Form.Control>
                  <Form.Input
                    type="hidden"
                    {...register('countryPhone')}
                    name="countryPhone"
                  />
                  <Phone
                    country={locale}
                    onChange={updatePhone}
                    error={errors.phone}
                    inputProps={{
                      id: 'form_phone',
                      name: 'phone',
                      disabled: loading,
                    }}
                  />
                  {errors.phone && (
                    <Form.Error>{errors.phone?.message}</Form.Error>
                  )}
                </Form.Control>
              </Form.Field>
            </Col>
          </Row>

          <Row>
            <Col xs={12} sm={6}>
              <Form.Field>
                <Form.Label htmlFor="form_password">
                  {t('form.step2.password.label')}
                </Form.Label>
                <Form.Control>
                  <Form.Input
                    error={errors.password}
                    disabled={loading}
                    type="password"
                    id="form_password"
                    name="password"
                    {...register('password')}
                    placeholder={t('form.step2.password.placeholder')}
                  />
                </Form.Control>
              </Form.Field>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Field>
                <Form.Label htmlFor="form_confirmation_password">
                  {t('form.step2.confirmation_password.label')}
                </Form.Label>
                <Form.Control>
                  <Form.Input
                    error={errors.confirmation_password}
                    disabled={loading}
                    type="password"
                    id="form_confirmation_password"
                    name="confirmation_password"
                    {...register('confirmation_password')}
                    placeholder={t(
                      'form.step2.confirmation_password.placeholder'
                    )}
                  />
                </Form.Control>
              </Form.Field>
            </Col>
            <Col xs={12} sm={12}>
              <Form.PasswordCheck
                rules={['minLength', 'number', 'capital', 'match']}
                minLength={7}
                value={password}
                valueAgain={confirmationPassword}
                iconSize={10}
                className="passwordChecklist"
                messages={{
                  minLength: t('form.step2.password.minLength'),
                  number: t('form.step2.password.number'),
                  capital: t('form.step2.password.capital'),
                  match: t('form.step2.password.match'),
                }}
                onChange={(isValid) => {}}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <Form.Field>
                <Form.Control>
                  <Form.Checkbox
                    error={errors.conditions}
                    htmlFor="form_conditions"
                    disabled={loading}
                    id="form_conditions"
                    name="conditions"
                    {...register('conditions')}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('form.step2.conditions.label'),
                      }}
                    ></span>
                  </Form.Checkbox>
                  {errors.conditions && (
                    <Form.Error>{errors.conditions?.message}</Form.Error>
                  )}
                </Form.Control>
              </Form.Field>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Control>
                <Form.RadioButtonList
                  htmlFor="form_communication_acceptance"
                  disabled={loading}
                  id="form_communication_acceptance"
                  name="communication_acceptance"
                  {...register('communication_acceptance')}
                  title={t('form.step2.communication_acceptance.label')}
                  error={errors.communication_acceptance}
                  options={[
                    {
                      value: true,
                      label: t('form.step2.communication_acceptance.yes'),
                    },
                    {
                      value: false,
                      label: t('form.step2.communication_acceptance.no'),
                    },
                  ]}
                >
                  {errors.communication_acceptance && (
                    <Form.Error>{errors.communication_acceptance?.message}</Form.Error>
                  )}
                </Form.RadioButtonList>
                </Form.Control>
              </Col>
          </Row>

          {errors.apiError && (
            <Notice color="red">
              <span
                dangerouslySetInnerHTML={{ __html: errors.apiError?.message }}
              ></span>
            </Notice>
          )}

          <Form.Button disabled={loading}>
            {t('form.step2.button')}
            {loading && <Form.Loader />}
          </Form.Button>
        </Form>
      </div>
    </CardContent>
  );
};

export default Step2Register;
